import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Page } from '@components/page';
import { LeftOutlined } from '@ant-design/icons';
import { SelectItemsHeader } from './components/select-items-header';
import {
    SelectItemsBody,
    SelectItemsBodyBonification,
} from './components/select-items-body';
import { SelectItemsActions } from './components/select-items-actions';
import { SelectDeliveryCustomerModal } from './components/select-delivery-customer-modal';
import { ChangeDiscountContextProvider } from 'src/contexts/change-discount-modal-context';
import { ChangeDiscountModal } from './components/change-discount-modal';
import {
    customerSelector,
    draftsSelector,
    fetchAvailableItems,
    fetchAvailablePaymentConditions,
    fetchConvenio,
    fetchCustos,
    fetchMoedas,
    fetchMoedasSimbolos,
    fetchOrder,
    fetchPaymentCondition,
    fetchPdiscqtWithType,
    fetchSemaphores,
    orderItemsSelector,
    orderTypeSelector,
    resetBonification,
    updateCampaignItems,
    updateMoeda,
    updateOrder,
} from '@redux/slices/order';
import { useQuery } from '@hooks/use-query';
import { scheduledSelector } from '@redux/slices/order/scheduled';
import { historySelector } from '@redux/slices/order/history';
import { calculateItemSemaphore } from '@redux/functions/order';
import { RootState } from '@redux/reducers';
import { DiscountWarning } from '@components/warning';
import { OrderContextProvider } from './context/provider';
import { campaignSelector, selectCampaign } from '@redux/slices/campaign';
import { fetchMinValueForOrder } from '@redux/slices/min-order';
import { translate } from '@components/i18n';
import { formatMoney } from '@utils/money';
import { AppDispatch } from '@redux/store';
import { IS_ARG } from '@config/env';
import { fetchParameterValues } from '@redux/slices/config';
import { clearState as resetSelectTypeState } from '@redux/slices/order/select-type';
import { updatePaymentCondition } from '@redux/slices/order/select-items';

export const SelectItems: React.FC = () => {
    const firstLoadDraftItemsRef = useRef(true)
    const firstLoadDraftCampaignRef = useRef(true)

    const [loading, setLoading] = useState(true);
    const history = useHistory();
    const query = useQuery();
    const dispatch = useDispatch<AppDispatch>();
    const { customer } = useSelector(customerSelector);
    const { orderType } = useSelector(orderTypeSelector);
    const { drafts } = useSelector(draftsSelector);
    const { scheduled } = useSelector(scheduledSelector);
    const { history: historyState } = useSelector(historySelector);
    const rootState = useSelector((state: RootState) => state);
    const campaignsState = useSelector(campaignSelector);
    const orderState = useSelector(orderItemsSelector);
    const [canFetchItems, setCanFetchItems] = useState<boolean>(false)

    const readOnly = query.get('readOnly') === 'true';

    const {
        discountPanteci,
        semaphores,
        custos,
        convenios,
        pdiscqt,
        order: orderHistory,
        selectedMoeda,
        moedaSimbolos,
        moedas,
        paymentCondition,
        ...selectItemsState
    } = useSelector(orderItemsSelector);

    const isDraft = query.get('draft') === 'true';
    const scheduledOrder = query.get('scheduled') === 'true'

    const goBack = (): void => history.goBack();

    useEffect(() => {
        dispatch(fetchMoedas())
        dispatch(fetchMoedasSimbolos())

        if (IS_ARG) {
            // to verify if is ARG, because this config is only for ARG
            // minOrderValue is configured in Config page
            dispatch(fetchParameterValues())
        }
    }, [])


    const fetchPreData = async (): Promise<void> => {
        const arrayDispatch = []
        if (isDraft && moedas.length > 0 && moedaSimbolos.length > 0) {
            const moeda = moedas.find(item => item.codigo === orderHistory?.codigoMoeda)
            const simbolo = moedaSimbolos.find(item => item.codigoMoeda === orderHistory?.codigoMoeda)

            if (moeda) {
                dispatch(updateMoeda({
                    ...moeda,
                    simbolo: simbolo?.simbolo
                }))

                arrayDispatch.push(dispatch(fetchCustos({ moeda: moeda.codigo })).unwrap())
            }
        } else if (!isDraft && selectedMoeda?.codigo) {
            arrayDispatch.push(dispatch(fetchCustos({ moeda: selectedMoeda.codigo })).unwrap())
        }

        await Promise.allSettled([
            dispatch(fetchConvenio()).unwrap(),
            dispatch(fetchPdiscqtWithType(orderHistory.customer.typeId)).unwrap(), //
            dispatch(fetchSemaphores()).unwrap(),
            dispatch(fetchPaymentCondition({ data: orderHistory.customer.code })).unwrap(),
            dispatch(fetchAvailablePaymentConditions()).unwrap(),
            dispatch(resetBonification()),
            ...arrayDispatch,
        ])
    }

    useEffect(() => {
        if (isDraft) {
            const ref = query.get('ref');
            const order = scheduledOrder
                ? scheduled?.items?.find((d) => `${d.draftRef}` === ref)
                : readOnly
                    ? historyState.find((d) => `${d.draftRef}` === ref)
                    : drafts?.items?.find((d) => `${d.draftRef}` === ref);


            dispatch(resetSelectTypeState())

            // carrega pedido
            dispatch(fetchOrder({ data: { id: Number(ref), order } }))
        }
    }, [])

    useEffect(() => {
        if (!isDraft && paymentCondition?.id) {
            dispatch(updatePaymentCondition({ id: paymentCondition.id, description: paymentCondition.description }));
        }
    }, [paymentCondition?.id])

    useEffect(() => {
        if (orderHistory?.customer?.code && moedaSimbolos?.length && moedas?.length) {
            fetchPreData().then(() => {
                setCanFetchItems(true)
                setLoading(false);
            })
        }
    }, [orderHistory?.customer?.code, moedaSimbolos?.length, moedas?.length])

    useEffect(() => {
        if (!isDraft) {
            dispatch(updateOrder({ customer, orderType }));
            setLoading(false);

            if (!selectedMoeda?.codigo) {
                history.replace("/create-order/select-type")
            }
        }
    }, []);

    // apenas chama essa funcao em novo pedido
    const getItems = async (): Promise<void> => {
        const itensWithQtd = selectItemsState.availableItems.filter(item => item.quantity > 0)
        if (
            orderHistory.paymentCondition.id &&
            selectItemsState.availablePaymentConditions.length > 0 &&
            !isDraft
        ) {
            if (!selectItemsState.draftRef) {

                dispatch(fetchAvailableItems({
                    paymentConditionId: orderHistory.paymentCondition.id,
                    tempItems: itensWithQtd,
                }));
            }
        } else if (
            orderHistory?.paymentCondition?.id &&
            !selectItemsState.request?.paymentConditionId &&
            !isDraft
        ) {
            message.info({
                content: translate('general.suggestionNotFound'),
                key: 'suggestion-not-found',
            });
            dispatch(
                fetchAvailableItems({
                    paymentConditionId: orderHistory.paymentCondition.id,
                    tempItems: itensWithQtd,
                }),
            );
        }
    }

    // apenas chama essa funcao em draft
    const getItemsDraft = async (): Promise<void> => {
        if (orderHistory?.paymentCondition?.id) {
            // itens do pedido no estado ou do pedido dependendo se for primeiro load
            // se houver alteração do estado que cause um reload, buscará os itens novamente, mas nao alterará o que ja feito/atualizado
            let items = []

            // carrega itens do pedido no estado quando for o primeiro load
            if (firstLoadDraftItemsRef.current && selectItemsState.orderApi.toOrderRequest().tempItems.length > 0) {
                items = selectItemsState.orderApi.toOrderRequest().tempItems
                firstLoadDraftItemsRef.current = false

                // a partir do segundo load, pega apenas do estado
            } else {
                items = selectItemsState.availableItems.filter(item => item.quantity > 0)
            }

            dispatch(fetchAvailableItems({
                paymentConditionId: orderHistory.paymentCondition.id,
                tempItems: items,
            }))
                .then(() => {
                    if (selectItemsState?.orderApi?.id && isDraft) {
                        // carrega campanha no estado quando for o primeiro load
                        if (
                            selectItemsState?.orderApi?.campanhaIds &&
                            selectItemsState?.orderApi?.campanhaIds.length > 0 &&
                            firstLoadDraftCampaignRef.current
                        ) {
                            firstLoadDraftCampaignRef.current = false
                            dispatch(selectCampaign(selectItemsState.orderApi.campanhaIds));
                        }

                    }
                })
        }
    }

    useEffect(() => {
        if (canFetchItems && !isDraft) {
            getItems()
        }
    }, [
        canFetchItems,
        orderState?.order?.orderType?.platform?.id,
        orderHistory?.paymentCondition?.id
    ]);

    useEffect(() => {
        if (
            canFetchItems &&
            isDraft &&
            orderState.order.customer?.code &&
            orderState.order.customer.typeId &&
            orderState.order.customer?.state &&
            orderState.order.orderType?.platform?.id &&
            orderHistory?.paymentCondition?.id &&
            custos && custos.length > 0
        ) {
            getItemsDraft()
        }
    }, [
        canFetchItems,
        orderState?.order?.orderType?.platform?.id,
        orderHistory?.paymentCondition?.id,
        orderState.order.customer,
        orderState.order.orderType?.platform?.id,
        custos,
    ]);

    useEffect(() => {
        if (campaignsState.availableCampaignForOrder.length > 0) {
            dispatch(updateCampaignItems());
        }

    }, [
        campaignsState.selectedCampaigns,
        campaignsState.availableCampaignForOrder,
    ]);

    useEffect(() => {
        dispatch(fetchMinValueForOrder());
    }, [orderState.availableItems, orderState.order.addCustomerSelected]);

    return (
        <OrderContextProvider>
            <Page
                title={translate('pages.newOrderItems')}
                greyBackground
                onMenuClick={goBack}
                menuIcon={<LeftOutlined />}
                loading={loading}
            >
                <Row
                    justify="center"
                    style={{
                        backgroundColor: '#fff490',
                        borderRadius: '4px',
                        padding: '5px',
                        display:
                            !!discountPanteci ||
                                !!orderHistory?.valorSuframa ||
                                !!orderHistory?.valorTrade
                                ? 'flex'
                                : 'none',
                    }}
                >
                    <Col span={6}>
                        {!!discountPanteci && (
                            <DiscountWarning
                                description={`${translate(
                                    'general.activatedEarlyDiscount',
                                )}(${discountPanteci}%)`}
                            />
                        )}
                        {!!orderHistory?.valorSuframa && (
                            <DiscountWarning
                                description={`${translate(
                                    'general.suframaDiscount',
                                )}: ${formatMoney(orderState?.selectedMoeda?.simbolo, orderHistory?.valorSuframa)}`}
                            />
                        )}
                        {!!orderHistory?.valorTrade && (
                            <DiscountWarning
                                description={`${translate(
                                    'general.tradeValue',
                                )}: ${formatMoney(orderState?.selectedMoeda?.simbolo, orderHistory?.valorTrade)}`}
                            />
                        )}
                    </Col>
                </Row>

                <ChangeDiscountContextProvider>
                    <SelectItemsHeader readOnly={!!readOnly} />
                    {!readOnly && <SelectItemsBody />}
                    {readOnly && (
                        <SelectItemsBodyBonification
                            key={`semaphores-${semaphores.length}`}
                            order={{
                                ...orderHistory,
                                availableItems: orderHistory.tempItems?.map(
                                    (i) => {
                                        const semaphore =
                                            calculateItemSemaphore(
                                                { ...i },
                                                false,
                                                rootState,
                                            );

                                        return { ...i, semaforo: semaphore };
                                    },
                                ),
                            }}
                        />
                    )}
                    {!readOnly && <SelectItemsActions />}
                    <SelectDeliveryCustomerModal />
                    <ChangeDiscountModal />
                </ChangeDiscountContextProvider>
            </Page>
        </OrderContextProvider>
    );
};
